<template>
  <div class="hf">
    <div class="hf-header">
      <button class="hf-back" @click="$router.push('/chat')" title="Back to Chats">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
      </button>
      <h1 class="hf-title">Files</h1>
      <div class="hf-search-wrap">
        <div class="hf-search">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#8a8a8e" stroke-width="2.5" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="21" y2="21"/></svg>
          <input v-model="searchUserText" type="text" placeholder="Search by user..." class="hf-search-input" @input="onSearchUser"/>
          <button v-if="searchUserText" class="hf-search-clear" @click="clearSearch">&times;</button>
        </div>
        <div v-if="showUserDropdown && filteredUsers.length" class="hf-user-dropdown">
          <div v-for="u in filteredUsers" :key="u.id" class="hf-user-item" @click="selectUser(u)">
            <UserAvatar :user_id="u.id" :size="'24'"/>
            <span>{{ u.text }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="hf-tabs">
      <button v-for="tab in [{key:'media',label:'Media'},{key:'doc',label:'Docs'},{key:'link',label:'Links'}]" :key="tab.key" class="hf-tab" :class="{ active: homeFileOption === tab.key }" @click="switchTab(tab.key)">{{ tab.label }}</button>
    </div>

    <!-- Selected user pill -->
    <div v-if="searchUser" class="hf-active-filter">
      <span class="hf-filter-pill">
        <UserAvatar :user_id="searchUser" :size="'18'"/>
        {{ selectedUserName }}
        <button class="hf-filter-close" @click="clearSearch">&times;</button>
      </span>
    </div>

    <!-- Loading -->
    <div v-if="localLoading" class="hf-empty">
      <div class="hf-spinner"></div>
      <p>Loading files...</p>
    </div>

    <!-- No results -->
    <div v-else-if="files.length === 0" class="hf-empty">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#c7c7cc" stroke-width="1.5" stroke-linecap="round"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
      <p>No files found</p>
    </div>

    <!-- Files grid -->
    <div v-else class="hf-grid-wrap">
      <template v-for="(item, index) in files">
        <div v-if="showMonth(index)" :key="`date-${index}`" class="hf-date-header">
          {{ item.creation_date | formatDate }}
        </div>

        <div :key="item.chat_id" class="hf-card" :class="checkResource(item)">
          <!-- Image -->
          <template v-if="checkResource(item) === 'image' && item.url">
            <div class="hf-thumb" :style="{ backgroundImage: `url(${item.url})` }" @click="showImagePreview(item.url)">
              <div v-if="item.sender_id" class="hf-card-avatar">
                <UserAvatar :user_id="item.sender_id.split('/').pop()" :size="'22'"/>
              </div>
              <button class="hf-download-btn" @click.stop.prevent="downloadHomeFile(item)" aria-label="Download" title="Download">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              </button>
            </div>
            <div class="hf-card-info">
              <span class="hf-card-type">Image</span>
              <span class="hf-card-date" v-if="item.creation_date">{{ formatFileDate(item.creation_date) }}</span>
            </div>
          </template>

          <!-- Video -->
          <template v-else-if="checkResource(item) === 'video' && item.url">
            <div class="hf-thumb" :style="{ backgroundImage: `url(${item.preview_url || item.url})` }" @click="showVideoPreview(item.url, item.preview_url)">
              <div class="hf-play">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="#fff"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              </div>
              <button class="hf-download-btn" @click.stop.prevent="downloadHomeFile(item)" aria-label="Download" title="Download">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              </button>
            </div>
            <div class="hf-card-info">
              <span class="hf-card-type">Video</span>
              <span class="hf-card-date" v-if="item.creation_date">{{ formatFileDate(item.creation_date) }}</span>
            </div>
          </template>

          <!-- Document -->
          <template v-else-if="(checkResource(item) === 'pdf' || checkResource(item) === 'doc') && item.url">
            <div class="hf-thumb hf-doc-thumb" @click="create_image_from_pdf(item)">
              <img v-if="item.preview_url" :src="item.preview_url" class="hf-doc-preview"/>
              <svg v-else width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#3478f6" stroke-width="1.5" stroke-linecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
              <button class="hf-download-btn hf-download-btn-light" @click.stop.prevent="downloadHomeFile(item)" aria-label="Download" title="Download">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3478f6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              </button>
            </div>
            <div class="hf-card-info">
              <span class="hf-card-type">{{ checkResource(item) === 'pdf' ? 'PDF' : 'Document' }}</span>
              <span class="hf-card-date" v-if="item.creation_date">{{ formatFileDate(item.creation_date) }}</span>
            </div>
          </template>

          <!-- Media generic -->
          <template v-else-if="checkResource(item) === 'media' && item.url">
            <div class="hf-thumb" :style="{ backgroundImage: `url(${item.url})` }" @click="showImagePreview(item.url)">
              <button class="hf-download-btn" @click.stop.prevent="downloadHomeFile(item)" aria-label="Download" title="Download">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              </button>
            </div>
            <div class="hf-card-info">
              <span class="hf-card-type">Media</span>
              <span class="hf-card-date" v-if="item.creation_date">{{ formatFileDate(item.creation_date) }}</span>
            </div>
          </template>

          <!-- Link -->
          <template v-else-if="checkResource(item) === 'link'">
            <a :href="verifyUrl(item.url)" target="_blank" class="hf-link">
              <div v-if="getYoutubeThumbnail(item.url)" class="hf-thumb" :style="{ backgroundImage: `url(${getYoutubeThumbnail(item.url)})` }"></div>
              <div v-else class="hf-thumb hf-link-icon-thumb">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3478f6" stroke-width="1.5" stroke-linecap="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
              </div>
              <div class="hf-link-info">
                <span class="hf-link-title">{{ linkTitles[item.url] || item.description || truncateUrl(item.url) }}</span>
                <span class="hf-link-domain">{{ getDomainFromUrl(item.url) }}</span>
                <span class="hf-card-date" v-if="item.creation_date">{{ formatFileDate(item.creation_date) }}</span>
              </div>
            </a>
          </template>

          <!-- Unknown -->
          <template v-else>
            <div class="hf-thumb hf-doc-thumb">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#c7c7cc" stroke-width="1.5" stroke-linecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            </div>
            <div class="hf-card-info">
              <span class="hf-card-type">{{ item.type || 'Unknown' }}</span>
              <span v-if="!item.url" class="hf-card-type" style="color: #ff3b30">No URL</span>
              <span class="hf-card-date" v-if="item.creation_date">{{ formatFileDate(item.creation_date) }}</span>
            </div>
          </template>
        </div>
      </template>
    </div>

    <!-- Modals -->
    <invoice-modal :show="showInvoiceModal" :hide="hideInvoiceModal" :url="url_temp_invoice"
                   :sender_profile_picture_invoice="sender_profile_picture_invoice"/>
    <invoice-ready-modal :show="showInvoiceReadyModal" :hide="hideInvoiceReadyModal" :url="url_temp_invoice"
                         :sender_profile_picture_invoice="sender_profile_picture_invoice"/>
    <v-photoswipe
        :isOpen="previewImageData.isOpen"
        :items="previewImageData.items"
        :options="previewImageData.options"
        @close="hideImagePreview"
    />
  </div>
</template>

<script>
import {mapState, mapActions} from "vuex";
import config from "@/config/constants.js";
import {PhotoSwipe} from 'v-photoswipe';
import UserAvatar from '@/components/UserAvatar';
import moment from 'moment'
import invoiceModal from "./modals/invoice";
import invoiceReadyModal from "./modals/invoice_ready";
import {isVideo, isImage} from "@/util/video_utils_v2";
import {firestore} from "@/config/firebase";

let token = localStorage.getItem("auth_token");

const pdfjsLib = require('pdfjs-dist-for-node');

export default {
  name: "HomeFiles",
  mounted() {
    const self = this;
    this.$store.dispatch("Users_list/clear_data");
    setTimeout(() => {
      try {
        window.removeEventListener('scroll', self.handleScrollFiles);
      } catch (error) {
        // ignore scroll cleanup error
      }
      window.addEventListener('scroll', self.handleScrollFiles);
    }, 100);
  },
  created() {
    if (!this.tenant_id || !this.owner_id) {
      if (this.user) {
        this.$store.commit('Chat/SET_USER_DATA', {
          tenant_id: this.user.tenant_id || 'default-tenant',
          owner_id: this.user.owner_id || 'test-user-001',
          device_id: 'web'
        });
      }
    }

    if (this.$store.getters["Chat/isLoggedIn"] || this.user) {
      this.addFiles();
    } else {
      this.$router.push("/chat");
    }
  },
  computed: {
    ...mapState({
      status: state => state.Chat.status,
      owner_id: state => state.Chat.owner_id,
      device_id: state => state.Chat.device_id,
      tenant_id: state => state.Chat.tenant_id,
      users: state => state.Chat.uxDocs,
      user: (state) => state.Authenticate.user,
      files() {
        return this.$store.state.Chat.files || [];
      },
      dataMounths: state => state.Chat.dataMounths
    }),
    xparticipants() {
      let users = [];
      for (let i of Object.keys(this.users)) {
        users.push(this.users[i]);
      }
      return users;
    },
    filteredUsers() {
      if (!this.searchUserText) return [];
      const q = this.searchUserText.toLowerCase();
      return this.xparticipants.filter(u => u.text && u.text.toLowerCase().includes(q)).slice(0, 8);
    },
    selectedUserName() {
      if (!this.searchUser) return '';
      const u = this.xparticipants.find(x => x.id === this.searchUser);
      return u ? u.text : '';
    },
  },
  filters: {
    formatDate: function (value) {
      if (value) {
        return moment(value).format('MMMM YYYY');
      } else {
        return 'NaN'
      }
    },
  },
  data() {
    return {
      localLoading: true,
      lastVisibleDoc: null,
      apiUrl: config.api_url + "url_metadata",
      showSuggestionFilesList: false,
      searchFiles: "",
      searchUser: "",
      searchUserText: "",
      showUserDropdown: false,
      usersIdsFiles: [],
      homeFileOption: 'media',
      linkTitles: {},
      another: false,
      options: {
        itemsPerPage: -1,
      },
      selected: [],
      title: "",
      text: "",
      rutaExel:
          config.api_url + "download_exel?user_role=client&token=" + token,
      search: "",
      previewImageData: {
        isOpen: false,
        items: [
          {
            src: '',
            w: 0,
            h: 0,
          }
        ],
        options: {
          index: 0,
          closeEl: true,
          captionEl: false,
          fullscreenEl: false,
          zoomEl: false,
          shareEl: false,
          counterEl: false,
          arrowEl: false,
          preloaderEl: true,
        }
      },
      showInvoiceModal: false,
      showInvoiceReadyModal: false,
      url_temp_invoice: null,
      sender_profile_picture_invoice: null,
      bottom: false,
      bottomLoading: false,
    };
  },
  components: {
    'v-photoswipe': PhotoSwipe,
    UserAvatar,
    "invoice-modal": invoiceModal,
    "invoice-ready-modal": invoiceReadyModal,
  },
  methods: {
    ...mapActions({get_users: "Users_list/get_users"}),

    onSearchUser() {
      this.showUserDropdown = true;
      if (!this.searchUserText) {
        this.clearSearch();
      }
    },
    selectUser(u) {
      this.searchUser = u.id;
      this.searchUserText = u.text;
      this.showUserDropdown = false;
      this.selectSuggestionUserFile();
    },
    clearSearch() {
      this.searchUser = "";
      this.searchUserText = "";
      this.showUserDropdown = false;
      this.selectSuggestionUserFile();
    },
    switchTab(tab) {
      this.homeFileOption = tab;
    },

    formatFileDate(date) {
      if (!date) return '';
      return moment(date).format('MMM DD');
    },

    truncateUrl(url) {
      if (!url) return '';
      const maxLength = 30;
      if (url.length <= maxLength) return url;
      return url.substring(0, maxLength) + '...';
    },

    getFileTypeLabel(file) {
      const type = this.checkResource(file);
      switch(type) {
        case 'image': return 'Image';
        case 'video': return 'Video';
        case 'pdf': return 'PDF';
        case 'doc': return 'Document';
        case 'link': return 'Link';
        case 'media': return 'Media';
        default: return file.type || 'File';
      }
    },

    normalizeFileData(fileData) {
      if (!fileData) {
        return {
          type: 'unknown',
          url: null,
          preview_url: null,
          sender_id: null,
          owner_ids: [],
          creation_date: new Date()
        };
      }

      return {
        type: fileData.type || 'unknown',
        url: this.fixUrl(fileData.url),
        preview_url: this.fixUrl(fileData.preview_url || fileData.url),
        sender_id: fileData.sender_id,
        owner_ids: fileData.owner_ids || [],
        creation_date: fileData.creation_date || new Date(),
        mime_type: fileData.mime_type,
        file_name: fileData.file_name,
        file_size: fileData.file_size,
        title: fileData.title,
        description: fileData.description,
        ...fileData
      };
    },

    fixUrl(url) {
      if (!url) return null;
      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      if (url.includes('localhost:9199') || url.includes('127.0.0.1:9199')) {
        if (!url.startsWith('http://')) return 'http://' + url.replace('http://', '').replace('https://', '');
        return url;
      }
      if (url.startsWith('/')) return window.location.origin + url;
      return 'https://' + url;
    },

    forceReload() {
      this.localLoading = true;
      this.bottomLoading = false;
      this.lastVisibleDoc = null;
      this.$store.commit("Chat/addFiles", {files: [], append: false});
      this.$nextTick(() => { this.addFiles(); });
    },

    bottomVisible() {
      const scrollY = window.scrollY;
      const visible = window.innerHeight;
      const pageHeight = document.documentElement.scrollHeight;
      const bottomOfPage = visible + scrollY >= pageHeight;
      return bottomOfPage || (pageHeight - (visible + scrollY)) <= 250;
    },

    addFiles() {
      if (!this.bottomLoading) {
        this.localLoading = true;
      }

      const tenantId = this.tenant_id || 'default-tenant';
      const ownerId = this.owner_id || 'test-user-001';
      const path = `/tenants/${tenantId}/users/${ownerId}`;

      return new Promise(resolve => {
        const self = this;
        let files_bulk = [];

        if (!self.bottomLoading) {
          self.bottomLoading = true;
        }

        let files = firestore
            .collection("tenants")
            .doc(tenantId)
            .collection("files")
            .orderBy('creation_date', 'desc')
            .limit(12);

        if (this.homeFileOption) {
          files = files.where("type", "==", this.homeFileOption);
        }

        if (self.searchUser) {
          const senderPath = `/tenants/${tenantId}/users/${self.searchUser}`;
          files = files.where("sender_id", "==", senderPath);
        }

        files = files.where("owner_ids", "array-contains", path);

        if (self.lastVisibleDoc && self.bottomLoading) {
          files = files.startAfter(self.lastVisibleDoc);
        } else {
          self.lastVisibleDoc = null;
        }

        files.get().then(querySnapshot => {
          if (!querySnapshot.empty) {
            self.lastVisibleDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
          }

          self.localLoading = false;

          if (querySnapshot.empty) {
            if (!self.lastVisibleDoc) {
              self.$store.commit('Chat/addFiles', {files: [], append: false});
            }
            self.bottomLoading = false;
            resolve();
            return;
          }

          querySnapshot.forEach(doc => {
            let data = self.normalizeFileData(doc.data());
            data = { ...data, chat_id: doc.id, message_id: doc.id };
            files_bulk.push(data);
          });

          const shouldAppend = self.bottomLoading && self.files.length > 0;
          const existingIds = new Set(self.files.map(f => f.chat_id));
          const uniqueFiles = files_bulk.filter(f => !existingIds.has(f.chat_id));

          if (uniqueFiles.length > 0) {
            self.$store.commit('Chat/addFiles', {
              files: uniqueFiles,
              append: shouldAppend
            });
          }

          self.bottomLoading = false;

          if (self.homeFileOption === 'link') {
            self.$nextTick(() => self.fetchLinkTitles());
          }

          resolve();
        }).catch((error) => {
          console.error('Home files query error:', error);
          self.localLoading = false;
          self.bottomLoading = false;
          resolve();
        });
      });
    },

    handleScrollFiles() {
      this.bottom = this.bottomVisible();
    },

    setter() {
      this.selected = [];
    },

    getMonth(date) {
      return moment(date).month();
    },

    get_excel(user_role) {
      this.$store.dispatch("Users_list/download", user_role);
    },

    checkResource(file) {
      const url = file.url || '';
      const mimeType = file.mime_type || '';
      const fileType = file.type || '';

      if (isVideo(file) || url.match(/\.(mp4|webm|ogg|mov|avi)$/i) || mimeType.includes('video')) return "video";
      if (isImage(file) || url.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i) || mimeType.includes('image')) return "image";
      if (url.match(/\.pdf$/i) || mimeType.includes('pdf') || fileType === 'pdf') return "pdf";
      if (url.match(/\.(doc|docx|txt|rtf)$/i) || mimeType.includes('document') || fileType === 'doc') return "doc";
      if (fileType === 'link') return "link";
      return fileType || 'unknown';
    },

    showImagePreview(url) {
      const self = this;
      const img = new Image();
      img.onload = function () {
        self.previewImageData.items[0].src = url;
        self.previewImageData.items[0].w = this.width;
        self.previewImageData.items[0].h = this.height;
        self.previewImageData.isOpen = true;
        self.$set(self.previewImageData.options, 'index', 0);
        if (document.getElementsByClassName('pswp__button--close').length > 0) {
          document.getElementsByClassName('pswp__button--close')[0].setAttribute('title', '');
        }
        if (document.getElementsByClassName('pswp__button--zoom').length > 0) {
          document.getElementsByClassName('pswp__button--zoom')[0].setAttribute('title', '');
        }
      };
      img.src = url;
    },

    showVideoPreview(url, previewUrl) {
      this.previewImageData.items[0] = {
        html: `<video width="100%" poster="${previewUrl}" height="100%" controls autoplay style="padding-top: 40px;"><source src="${url}" type="video/mp4"></video>`
      };
      this.previewImageData.isOpen = true;
      this.$set(this.previewImageData.options, 'index', 0);
      if (document.getElementsByClassName('pswp__button--close').length > 0) {
        document.getElementsByClassName('pswp__button--close')[0].setAttribute('title', '');
      }
      if (document.getElementsByClassName('pswp__button--zoom').length > 0) {
        document.getElementsByClassName('pswp__button--zoom')[0].setAttribute('title', '');
      }
    },

    hideImagePreview() {
      let pwVideos = document.querySelectorAll(".pswp__zoom-wrap video");
      for (const video of pwVideos) { video.remove(); }
      this.previewImageData.isOpen = false;
    },

    getHomeFileFilename(item) {
      if (!item) return 'download';
      return item.name || item.file_name || item.filename || item.original_name || item.id || 'download';
    },
    async downloadHomeFile(item) {
      const url = item && item.url;
      if (!url) return;
      const filename = this.getHomeFileFilename(item);
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('fetch failed: ' + response.status);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      } catch (e) {
        window.open(url, '_blank', 'noopener');
      }
    },

    selectSuggestionUserFile() {
      this.localLoading = true;
      this.bottomLoading = false;
      this.lastVisibleDoc = null;
      this.$store.commit("Chat/addFiles", {files: [], append: false});
      this.$nextTick(() => { this.addFiles(); });
    },

    showDate: function (index) {
      if (index === 0) return true;
      if (this.files[index].type !== this.homeFileOption) return;
      let prev = moment(this.files[index - 1].creation_date).format('ddd, MMM');
      let actual = moment(this.files[index].creation_date).format('ddd, MMM');
      return actual !== prev;
    },

    showMonth: function (index) {
      if (index === 0) return true;
      if (this.files[index].type !== this.homeFileOption) return;
      let prevMonth = moment(this.files[index - 1].creation_date).month();
      let actualMonth = moment(this.files[index].creation_date).month();
      return actualMonth !== prevMonth;
    },

    getDomainFromUrl(url) {
      if (!url) return '';
      try {
        return new URL(url).hostname.replace('www.', '');
      } catch { return url; }
    },

    fetchLinkTitles() {
      const links = this.files.filter(f => f.type === 'link' && f.url);
      for (const link of links) {
        if (this.linkTitles[link.url]) continue;
        const videoId = this.getYoutubeVideoId(link.url);
        if (videoId) {
          fetch(`https://noembed.com/embed?url=${encodeURIComponent(link.url)}`)
            .then(r => r.json())
            .then(data => {
              if (data.title) this.$set(this.linkTitles, link.url, data.title);
            })
            .catch((err) => {
              console.warn('Failed to fetch link title:', err);
            });
        }
      }
    },

    getYoutubeVideoId(url) {
      if (!url) return null;
      const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
      const longMatch = url.match(/[?&]v=([a-zA-Z0-9_-]+)/);
      return shortMatch ? shortMatch[1] : (longMatch ? longMatch[1] : null);
    },

    getYoutubeThumbnail(url) {
      if (!url) return null;
      const videoId = this.getYoutubeVideoId(url);
      return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
    },

    verifyUrl(url) {
      if (url.indexOf("http") === -1) return "http://" + url;
      return url;
    },

    async create_image_from_pdf(item) {
      if (item.sending_status === 'generated_pdf') {
        alert('We are generating the invoice, please wait ...');
      } else {
        let url_pdf = item.url;
        this.showInvoiceModal = true;
        this.url_temp_invoice = url_pdf;
        let context;
        setTimeout(async () => {
          document.querySelector("#page-loader").innerHTML = 'Please wait while we generate the preview ...';
          await pdfjsLib.getDocument({url: url_pdf}).then(async function (res) {
            let _CANVAS = document.querySelector('#pdf-canvas');
            context = _CANVAS.getContext('2d');
            context.clearRect(0, 0, _CANVAS.width, _CANVAS.height);
            let page = await res.getPage(1);
            let pdf_original_width = page.getViewport(1).width;
            let scale_required = _CANVAS.width / pdf_original_width;
            let viewport = page.getViewport(scale_required);
            _CANVAS.height = viewport.height;
            let render_context = { canvasContext: context, viewport: viewport };
            await page.render(render_context);
            document.querySelector("#pdf-canvas").style.display = 'block';
            document.querySelector("#page-loader").style.display = 'none';
          }).catch(() => {
            document.querySelector("#page-loader").innerHTML = 'Cannot preview this document ...';
          });
        }, 500);
      }
    },

    hideInvoiceModal() { this.showInvoiceModal = false; },
    hideInvoiceReadyModal() { this.showInvoiceReadyModal = false; },
  },
  watch: {
    searchFiles(val) {
      if (val) {
        this.showSuggestionFilesList = true;
        let search_list = [];
        for (let i in this.users) {
          if (this.users[i].text.toLowerCase().indexOf(val) !== -1) search_list.push(i);
        }
        this.usersIdsFiles = search_list;
      } else {
        this.showSuggestionFilesList = false;
      }
    },

    homeFileOption() {
      this.localLoading = true;
      this.bottomLoading = false;
      this.lastVisibleDoc = null;
      this.$store.commit("Chat/addFiles", {files: [], append: false});
      this.$nextTick(() => { this.addFiles(); });
    },

    bottom(bottom) {
      if (bottom && !this.bottomLoading && !this.localLoading && this.lastVisibleDoc && this.files.length > 0) {
        this.bottomLoading = true;
        this.addFiles();
      }
    }
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScrollFiles);
  }
};
</script>

<style scoped lang="scss">
@import './_variables';

.hf {
  max-width: 960px;
  margin: 0 auto;
  padding: 20px 16px;
  font-family: $font;
  height: 100%;
  overflow-y: auto;
}

.hf-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 16px;
}
.hf-back {
  width: 32px;
  height: 32px;
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  color: #8e8e93;
  transition: all 0.15s;
  &:hover { background: rgba(0,0,0,0.04); color: #1a1a1a; }
}

.hf-title {
  font-size: 22px;
  font-weight: 700;
  color: $t1;
  font-family: $font;
  letter-spacing: -0.3px;
  margin: 0;
  flex-shrink: 0;
}

.hf-search-wrap {
  position: relative;
  flex: 1;
  max-width: 320px;
}

.hf-search {
  display: flex;
  align-items: center;
  height: 32px;
  background: $bg-input;
  border-radius: 8px;
  padding: 0 10px;
  gap: 6px;
}

.hf-search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 12px;
  font-family: $font;
  color: $t1;
  &::placeholder { color: $t3; }
}

.hf-search-clear {
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: rgba(0,0,0,0.15);
  border-radius: 50%;
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
  color: #fff;
  &:hover { background: rgba(0,0,0,0.25); }
}

.hf-user-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.12);
  z-index: 20;
  max-height: 260px;
  overflow-y: auto;
  padding: 4px;
}

.hf-user-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  color: $t1;
  &:hover { background: $hover; }
  &::v-deep .v-avatar { border: none !important; }
}

.hf-tabs {
  display: flex;
  gap: 2px;
  border-bottom: 1px solid $border;
  margin-bottom: 16px;
}

.hf-tab {
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  color: $t3;
  font-family: $font;
  border: none;
  background: transparent;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.15s;
  &:hover { color: $t1; }
  &.active { color: $accent; border-bottom-color: $accent; }
}

.hf-active-filter {
  margin-bottom: 12px;
}

.hf-filter-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 4px;
  background: $bg-input;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 500;
  color: $t1;
  font-family: $font;
  &::v-deep .v-avatar { border: none !important; }
}

.hf-filter-close {
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: rgba(0,0,0,0.1);
  border-radius: 50%;
  font-size: 12px;
  color: $t2;
  cursor: pointer;
  &:hover { background: rgba(0,0,0,0.2); }
}

.hf-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: $t3;
  p { margin-top: 12px; font-size: 13px; font-family: $font; }
}

.hf-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid $border;
  border-top-color: $accent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.hf-grid-wrap {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.hf-date-header {
  grid-column: 1 / -1;
  font-size: 14px;
  font-weight: 700;
  color: $t1;
  font-family: $font;
  padding: 8px 0 4px;
  border-bottom: 1px solid $border;
  &:first-child { padding-top: 0; }
}

.hf-card {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid $border;
  transition: box-shadow 0.2s;
  &:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.08); }
}

.hf-thumb {
  width: 100%;
  height: 180px;
  background-size: cover;
  background-position: center;
  background-color: $bg-input;
  cursor: pointer;
  position: relative;
}

.hf-doc-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.hf-doc-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hf-play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 48px;
  height: 48px;
  background: rgba(0,0,0,0.45);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  svg { margin-left: 3px; }
}

.hf-card-avatar {
  position: absolute;
  bottom: 6px;
  right: 6px;
  background: rgba(255,255,255,0.85);
  border-radius: 50%;
  padding: 2px;
  &::v-deep .v-avatar { border: none !important; }
}

.hf-download-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  z-index: 3;
  transition: background 0.15s ease, transform 0.15s ease;
  &:hover { background: rgba(0, 0, 0, 0.8); transform: scale(1.05); }
  &:focus { outline: none; }
  svg { display: block; }
}

.hf-download-btn-light {
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 1px 4px rgba(0,0,0,0.1);
  &:hover { background: #fff; }
}

.hf-card-info {
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.hf-card-type {
  font-size: 11px;
  font-weight: 600;
  color: $t2;
  font-family: $font;
}

.hf-card-date {
  font-size: 10px;
  color: $t3;
  font-family: $font;
}

.hf-link {
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
}

.hf-link-icon-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba($accent, 0.06);
}

.hf-link-info {
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.hf-link-title {
  font-size: 12px;
  font-weight: 600;
  color: $t1;
  font-family: $font;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.hf-link-domain {
  font-size: 10px;
  color: $t3;
  font-family: $font;
}

@media (max-width: 768px) {
  .hf { padding: 16px 12px; }
  .hf-title { font-size: 18px; }
  .hf-search-wrap { max-width: 200px; }
  .hf-grid-wrap { grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 10px; }
  .hf-thumb { height: 150px; }
}

@media (max-width: 480px) {
  .hf { padding: 12px 8px; }
  .hf-header { flex-direction: column; align-items: flex-start; gap: 8px; }
  .hf-search-wrap { max-width: 100%; width: 100%; }
  .hf-grid-wrap { grid-template-columns: repeat(2, 1fr); gap: 8px; }
  .hf-thumb { height: 130px; }
  .hf-tab { padding: 6px 12px; font-size: 11px; }
}

@media (max-width: 360px) {
  .hf-grid-wrap { grid-template-columns: 1fr; }
  .hf-thumb { height: 200px; }
}
</style>
